import { useNavigate, useParams } from "react-router-dom";
import { BeforeRecordingContent } from "../PrepareRecordingModal/BeforeRecording";
import { VerticalFlex, Space, ModalSize, ButtonVariant, Button, Modal, HorizontalFlex, MicOnIcon, IconSize } from "@enxcs/dap-ui-stratos/";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useQueryClient } from "@tanstack/react-query";
import { PrepareRecordingProvider } from "@/contexts/PrepareRecording/PrepareRecordingProvider";
import { usePrepareRecording } from "@/contexts/PrepareRecording/usePrepareRecording";
import { useRecordingStatus } from "@/contexts/RecordingStatus/useRecordingStatus";
import { RecordingStatusProvider } from "@/contexts/RecordingStatus/RecordingStatusProvider";
import { getRecordingTranscription } from "@/services/getRecordingTranscription";
import { AxiosError } from "axios";
import { RecordStatusEnum } from "@/pages/RecordsList/types";

interface BeforeRecordingModalProps {
  recordingUUID: string;
  onClose(): void;
}

const BeforeRecordingModalContent = ({ recordingUUID, onClose }: BeforeRecordingModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const selectedLanguage = usePrepareRecording((state) => state.selectedLanguage);
  const isAudioActive = usePrepareRecording((state) => state.isAudioActive);
  const selectedAudioType = usePrepareRecording((state) => state.selectedAudioType);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { errorHandler } = useErrorHandler();

  const { isRecording } = useRecordingStatus();

  const queryClient = useQueryClient();
  const { lang } = useParams();

  const handleOnClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleStartRecording = async () => {
    try {
      setIsLoading(true);
      const recordingInfo = await getRecordingTranscription({ recordingId: recordingUUID });

      if (!recordingInfo || typeof recordingInfo === "string" || recordingInfo instanceof AxiosError) {
        throw new Error(`Recording data not found for recordingUUID: ${recordingUUID}`);
      }

      if (recordingInfo.status !== RecordStatusEnum.READY_FOR_RECORDING) {
        throw new Error(`Recording status is not ready for recording - recordingUUID: ${recordingUUID}, Status:${recordingInfo.status}`);
      }

      navigate(`/${lang}/recording/${recordingUUID}`, {
        state: {
          selectedLanguage,
          audioInput: selectedAudioType,
        },
      });
      handleOnClose();
    } catch (error) {
      errorHandler(error, {
        showToast: true,
        eventName: "START-RECORDING",
        properties: {
          recordingId: recordingUUID,
          reason: `could not start recording. Error: ${error}.`,
        },
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: ["recordingSubjects"] });
      setIsLoading(false);
    }
  };

  return (
    <Modal
      size={ModalSize.S}
      title={t("prepareModal.beforeRecordingStep.title")}
      closeTitle={t("buttons.close")}
      body={
        <VerticalFlex $gap={Space.V24}>
          <BeforeRecordingContent />
        </VerticalFlex>
      }
      onClose={handleOnClose}
      footer={
        <HorizontalFlex $justify="flex-end" $gap={Space.V8}>
          <Button
            variant={ButtonVariant.PRIMARY}
            label={t("buttons.startRecording")}
            leftIcon={() => <MicOnIcon size={IconSize.V16} />}
            onClick={handleStartRecording}
            disabled={isLoading || !isAudioActive || isRecording}
          />
        </HorizontalFlex>
      }
    />
  );
};

const BeforeRecordingModal = (props: BeforeRecordingModalProps) => {
  return (
    <PrepareRecordingProvider>
      <RecordingStatusProvider>
        <BeforeRecordingModalContent {...props} />
      </RecordingStatusProvider>
    </PrepareRecordingProvider>
  );
};

export { BeforeRecordingModal };
